import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import {
  GITHUB_ORG_PATTERN,
  INVALID_ORG_LOGIN,
  type InvalidField,
  type RepositoryAccessConfig,
  VAULT_TYPES,
  findInvalidHostingField,
  findInvalidVaultField,
  findSecretNameProblem,
  toSecretPartialArn,
  toSecretResourceName,
} from "@p0security/integrations-github-shared";
import {
  p0InvokerEmail,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { DevEnv } from "@p0security/shared/types/environment";
import { ValidationError } from "@p0security/shared/types/error";

type Hosting = RepositoryAccessConfig["hosting"];
type Vault = RepositoryAccessConfig["vault"];
type VaultType = Vault["type"];

/** Where the connector runs. Its vault is always in the same cloud. */
export type ConnectorCloud = Hosting["type"];

/** The installer inputs that one cloud's connector Terraform and shell script
 * are rendered from, named as on the `repository-access` install item.
 *
 * P0 stores each of them when the install item is created, and none can
 * change after, so what's rendered from a stored item stays the connector's.
 */
export type ConnectorInputs = {
  aws: {
    hosting: Extract<Hosting, { type: "aws" }>;
    privateKeySecretName: string;
    vault: Extract<Vault, { type: "aws-sm" }>;
  };
  gcp: {
    // P0 looks the service's URL up once the service exists, so what deploys
    // the service can't depend on it.
    hosting: Omit<Extract<Hosting, { type: "gcp" }>, "connectorServiceUri">;
    privateKeySecretName: string;
    vault: Extract<Vault, { type: "gcp-sm" }>;
  };
};

/** What the connector needs from the installer's context: the environment,
 * which names P0's AWS role, and the installed gcloud integration, which
 * records P0's service account. A `FrontendInstallContext` is one. */
export type ConnectorContext = DevEnv & { installed: Record<string, unknown> };

/** The arguments to {@link toAwsConnector} or {@link toGcpConnector}, for
 * one cloud. */
export type ConnectorArgs<C extends ConnectorCloud = ConnectorCloud> = {
  [K in C]: {
    cloud: K;
    context: ConnectorContext;
    /** The connector image to deploy, as `<tag>@sha256:<digest>`. Defaults to
     * the build P0 pins for `cloud`. */
    imageDigest?: string;
    inputs: ConnectorInputs[K];
    /** The GitHub organization's login, which is the install item's id. */
    org: string;
  };
}[C];

// The all-zero digest that stood in for the images before they were published.
const UNPUBLISHED_DIGEST = `sha256:${"0".repeat(64)}`;

// The connector build each cloud deploys: a p0-security-perimeter main build's
// sha-<commit> tag on Docker Hub, and that tag's digest. To move to a newer
// build, change both, and find the digest with
// `docker buildx imagetools inspect p0security/p0-connector-github:<tag>`,
// or `p0security/p0-connector-github-gcloud:<tag>` for Cloud Run.
const IMAGE_DIGESTS: Readonly<Record<ConnectorCloud, string>> = {
  aws: "sha-7815680@sha256:5952bb06e8dfdabc3e24f2f64258ecf4c28acd6cdb99d101f2a8df08bb9488aa",
  gcp: "sha-7815680@sha256:880533b96898605e816cfc34a4d90d445705e343d94fe3fa1b868da11fb6a75d",
};

/** P0's connector image for Lambda, on Docker Hub. */
export const LAMBDA_IMAGE = "p0security/p0-connector-github";

/** P0's connector image for Cloud Run, on Docker Hub, which Cloud Run deploys
 * from directly. */
export const CLOUD_RUN_IMAGE =
  "docker.io/p0security/p0-connector-github-gcloud";

// Docker caps a tag at 128 characters, and the AWS Terraform tags each copy of
// the image with its tag, a hyphen and the copy's 36-character id. The shell
// script's copy ids are shorter.
const IMAGE_DIGEST_PATTERN = /^\w[\w.-]{0,90}@sha256:[a-f0-9]{64}$/;

/** Whether `value` is a string that `pattern` matches. `pattern.test` alone
 * would test a missing value as the string "undefined". */
const matches = (pattern: RegExp, value: unknown): value is string =>
  typeof value === "string" && pattern.test(value);

/** Throws a {@link ValidationError} unless the org, and the fields that place
 * the connector and its vault, follow the rules P0 checks them by when the
 * install is created. Each field is checked trimmed, as P0 stores it. */
const validateLocation = ({ cloud, inputs, org }: ConnectorArgs) => {
  // The hosting and the vault are checked as the cloud being rendered,
  // whatever type they give themselves, so that every field the Terraform
  // and the shell script render is checked.
  const invalid: InvalidField | undefined = !matches(GITHUB_ORG_PATTERN, org)
    ? { field: "org", message: INVALID_ORG_LOGIN }
    : (findInvalidHostingField({ ...inputs.hosting, type: cloud }) ??
      findInvalidVaultField({ ...inputs.vault, type: VAULT_TYPES[cloud] }));
  if (invalid) throw new ValidationError(invalid.field, {}, invalid.message);
};

/** Throws a {@link ValidationError} unless `name` names the private key's
 * secret as a vault of type `vaultType` takes it, the way P0 checks it when
 * the install is created. */
const validateSecretName: (
  name: unknown,
  vaultType: VaultType
) => asserts name is string = (name, vaultType) => {
  if (typeof name !== "string" || !name.trim()) {
    throw new ValidationError(
      "privateKeySecretName",
      {},
      "Enter the name of the secret that holds the private key first. The Terraform grants the connector read on exactly that secret."
    );
  }
  const problem = findSecretNameProblem(name, vaultType);
  if (problem) throw new ValidationError("privateKeySecretName", {}, problem);
};

/** Where the private key's secret is, as P0 points the connector at it. */
type AwsPrivateKeySecret = Readonly<{
  /** The vault's account */
  accountId: string;
  /** The secret's ARN without its suffix (see `toSecretPartialArn`) */
  partialArn: string;
}>;

/** The private key's secret, named in the vault's account and region.
 *
 * @throws ValidationError if there's no secret name, or if it's one that P0
 * would refuse when the install is created.
 */
const toPrivateKeySecret = ({
  privateKeySecretName: name,
  vault,
}: ConnectorInputs["aws"]): AwsPrivateKeySecret => {
  validateSecretName(name, "aws-sm");
  return {
    accountId: vault.install.trim(),
    partialArn: toSecretPartialArn({
      accountId: vault.install,
      name,
      region: vault.secretsRegion,
    }),
  };
};

/** Throws a {@link ValidationError} unless `imageDigest` pins a published
 * image as `<tag>@sha256:<digest>`. */
const validateImageDigest = (imageDigest: unknown) => {
  if (!matches(IMAGE_DIGEST_PATTERN, imageDigest)) {
    throw new ValidationError(
      "imageDigest",
      {},
      "The connector image must be <tag>@sha256:<digest>."
    );
  }
  if (imageDigest.endsWith(`@${UNPUBLISHED_DIGEST}`)) {
    throw new ValidationError(
      "imageDigest",
      {},
      "The GitHub connector image hasn't been published yet."
    );
  }
};

/** The Lambda function's settings, as the Terraform and the shell script both
 * deploy it. */
export const LAMBDA_SETTINGS = {
  architecture: "x86_64",
  memorySize: 512,
  timeout: 60,
} as const;

/** How many days CloudWatch keeps the connector's logs, unless the customer
 * changes it. */
export const LOG_RETENTION_DAYS = 30;

/** The names of the connector role's inline policies, the same whichever way
 * the connector is deployed. */
export const POLICY_NAMES = {
  logs: "WriteConnectorLogs",
  privateKey: "ReadGitHubAppPrivateKey",
} as const;

/** The connector's description: its Lambda function's, or its Cloud Run
 * service's and its service account's display name. */
export const toConnectorDescription = (org: string) =>
  `P0 GitHub connector for ${org}`;

/** One organization's connector on AWS, as its Terraform and shell script
 * deploy it. Each value is trimmed, as P0 stores it. */
export type AwsConnector = Readonly<{
  /** The account the connector runs in */
  accountId: string;
  connectorName: string;
  connectorRegion: string;
  /** The digest that `imageTag` resolves to on Docker Hub */
  imageDigest: string;
  imageTag: string;
  /** The GitHub organization's login */
  org: string;
  /** The role P0 assumes in the connector's account, which invokes it */
  p0RoleName: string;
  /** The connector's own role, which Lambda assumes */
  roleArn: string;
  secret: AwsPrivateKeySecret;
  secretsRegion: string;
}>;

/**
 * Resolves the connector that one GitHub organization's Terraform and shell
 * script deploy, for the customer to deploy in their own cloud.
 *
 * Both grant the connector read on the one secret that `privateKeySecretName`
 * names in the vault's account and region, by the partial ARN that P0 points
 * the connector at, so the installer needs that name before it can render
 * them. P0 fixes it when the install item is created.
 *
 * @throws ValidationError, with the input's path as its form id, for the first
 * input that the connector couldn't deploy with: a field P0 would refuse, a
 * missing `privateKeySecretName`, or an image that isn't published yet.
 */
export const toAwsConnector = (args: ConnectorArgs<"aws">): AwsConnector => {
  const { context, imageDigest = IMAGE_DIGESTS.aws, inputs, org } = args;
  validateLocation(args);
  const secret = toPrivateKeySecret(inputs);
  const accountId = inputs.hosting.accountId.trim();
  const connectorName = inputs.hosting.connectorName.trim();
  validateImageDigest(imageDigest);
  const [imageTag, digest] = imageDigest.split("@");
  return {
    accountId,
    connectorName,
    connectorRegion: inputs.hosting.connectorRegion.trim(),
    imageDigest: digest,
    imageTag,
    org,
    p0RoleName: awsRoleName(context, "iam-write"),
    // Only commercial regions get this far, so the partition is aws.
    roleArn: `arn:aws:iam::${accountId}:role/${connectorName}`,
    secret,
    secretsRegion: inputs.vault.secretsRegion.trim(),
  };
};

/** Whether the connector's private key is in another account than the
 * connector, and so needs a customer-managed KMS key. */
export const verifyCrossAccount = ({ accountId, secret }: AwsConnector) =>
  secret.accountId !== accountId;

/** The comment on the setting for the KMS key's ARN, as `#` lines that each
 * line after the first starts with `indent`. A secret in another account
 * needs a customer-managed key, and that account's policies must allow the
 * connector's role. */
export const renderKmsKeyComment = (connector: AwsConnector, indent = "") => {
  const { roleArn, secret } = connector;
  const lines = verifyCrossAccount(connector)
    ? [
        "The ARN of the KMS key that encrypts the secret. The secret is in",
        `account ${secret.accountId}, so it needs a customer-managed key: the default`,
        "aws/secretsmanager key can't be used from another account. Set this to",
        `that key's ARN. In account ${secret.accountId}, the secret's resource policy`,
        "must also allow the connector's role secretsmanager:GetSecretValue, and",
        "the key's policy must allow the role kms:Decrypt through Secrets Manager.",
        `The role is ${roleArn}.`,
      ]
    : [
        "The ARN of the KMS key that encrypts the secret, if that's a",
        "customer-managed key. The default aws/secretsmanager key needs nothing",
        "for a secret in this account.",
      ];
  return lines.map((line) => `# ${line}`).join(`\n${indent}`);
};

// A GitHub login can start with a digit, which a Terraform name can't, and one
// organization's names mustn't clash with another's in the same configuration.
const toTfId = (org: string) => tfId(`p0_github_${org.toLowerCase()}`);

/** The private key policy's lifecycle: a secret in another account than the
 * connector's needs a customer-managed key, and the plan fails until it's
 * set. */
const renderAwsSecretLifecycle = (id: string, connector: AwsConnector) =>
  verifyCrossAccount(connector)
    ? `

  lifecycle {
    precondition {
      condition     = split(":", local.${id}.private_key_secret_partial_arn)[4] == local.${id}.account_id || local.${id}.kms_key_arn != null
      error_message = "The private key's secret is in another AWS account, so it must be encrypted with a customer-managed KMS key. Set kms_key_arn to that key's ARN, and allow the connector's role in the secret's resource policy and in the key's policy."
    }
  }`
    : "";

/**
 * Renders the Terraform that deploys one GitHub organization's connector.
 *
 * It copies the image into ECR and deploys a Lambda function outside any VPC.
 * The connector's identity can read the GitHub App's private key secret and
 * nothing else, and P0's role can invoke it. An output gives the connector
 * hosting to enter in the installer.
 *
 * Every Terraform name carries the org's login, so several orgs' connectors
 * can share a configuration once their `required_providers` blocks are
 * merged.
 */
export const renderAwsTerraform = (connector: AwsConnector): string => {
  const {
    accountId,
    connectorName,
    connectorRegion,
    imageDigest: digest,
    imageTag,
    org,
    p0RoleName,
    secret,
    secretsRegion,
  } = connector;
  const id = toTfId(org);

  return `# P0's GitHub connector for ${org}, on AWS Lambda.
#
# Applying this copies P0's connector image from Docker Hub into an ECR
# repository, which Lambda requires. Run terraform where Docker and the AWS CLI
# are installed and the AWS CLI is signed in to account ${accountId}.
#
# P0 runs one connector for each organization, so apply this once, in one
# cloud. Other organizations' connectors can share this configuration, but each
# needs its own connector name: the connector's role, repository and log group
# are named after it, and these names are unique in an AWS account.

terraform {
  # terraform_data needs Terraform 1.4.
  required_version = ">= 1.4"
}

terraform {
  # If your configuration already has a required_providers block, add docker to
  # it and delete this block.
  required_providers {
    docker = {
      source  = "kreuzwerker/docker"
      version = "~> 3.0"
    }
  }
}

locals {
  ${id} = {
    # Where P0 invokes the connector, as entered in P0's installer. P0 calls
    # the connector exactly here, so don't change these: to change one, remove
    # the organization in P0 and add it again.
    account_id       = "${accountId}"
    connector_name   = "${connectorName}"
    connector_region = "${connectorRegion}"
    # The secret that holds the GitHub App's private key, by its partial ARN:
    # its ARN without the hyphen and six random characters that end it. P0
    # gives the connector this ARN. The connector can read this secret and no
    # other.
    private_key_secret_partial_arn = "${secret.partialArn}"
    secrets_region                 = "${secretsRegion}"
    ${renderKmsKeyComment(connector, "    ")}
    kms_key_arn = null
    # How many days CloudWatch keeps the connector's logs.
    log_retention_days = ${LOG_RETENTION_DAYS}
    # The role P0 assumes in this account, which invokes the connector. Don't
    # change it.
    p0_role_name = "${p0RoleName}"
    # P0's connector image: its tag on Docker Hub, and the digest that tag must
    # resolve to.
    image_tag    = "${imageTag}"
    image_digest = "${digest}"
  }
}

# P0 derives the connector's ARN from the account, region and name above, so the
# connector must be deployed exactly there. Add the credential settings your
# other AWS providers use, such as profile or assume_role, if they have any.
provider "aws" {
  alias               = "${id}"
  region              = local.${id}.connector_region
  allowed_account_ids = [local.${id}.account_id]
}

data "aws_partition" "${id}" {
  provider = aws.${id}
}

data "docker_registry_image" "${id}" {
  name = "${LAMBDA_IMAGE}:\${local.${id}.image_tag}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == local.${id}.image_digest
      error_message = "The connector image's tag doesn't resolve to the digest pinned here. Copy the current Terraform from P0's GitHub installer, or contact support@p0.dev."
    }
  }
}

resource "aws_ecr_repository" "${id}" {
  provider = aws.${id}

  name = local.${id}.connector_name
  # Tags can't be overwritten, so no one who can push to this repository can
  # replace the image that runs with access to the key.
  image_tag_mutability = "IMMUTABLE"
  force_delete         = true

  image_scanning_configuration {
    scan_on_push = true
  }
}

# Copies the pinned image from Docker Hub into the ECR repository, tagged with
# the image's tag and this copy's id. ECR's tags can't be overwritten, so each
# copy needs a tag of its own: copying the image again, to roll back or after
# an interrupted apply, pushes a new one.
resource "terraform_data" "${id}_image" {
  triggers_replace = {
    digest         = data.docker_registry_image.${id}.sha256_digest
    repository_url = aws_ecr_repository.${id}.repository_url
    tag            = local.${id}.image_tag
  }

  provisioner "local-exec" {
    # Signs Docker out of ECR at the end, so its login doesn't outlast the copy.
    # A failed sign-out doesn't fail the copy once the image is pushed.
    command = <<-EOT
      set -e
      trap 'docker logout "$REGISTRY" || true' EXIT
      aws ecr get-login-password --region "$REGION" |
        docker login --username AWS --password-stdin "$REGISTRY"
      docker pull --platform linux/amd64 "$SOURCE"
      docker tag "$SOURCE" "$TARGET"
      docker push "$TARGET"
    EOT

    environment = {
      REGION   = local.${id}.connector_region
      REGISTRY = split("/", aws_ecr_repository.${id}.repository_url)[0]
      SOURCE   = "${LAMBDA_IMAGE}@\${local.${id}.image_digest}"
      TARGET   = "\${aws_ecr_repository.${id}.repository_url}:\${local.${id}.image_tag}-\${self.id}"
    }
  }
}

# Pulling one platform and pushing it gives ECR that image's own manifest, not
# the tag's multi-platform one, so the digest there can differ from the one
# pinned above. Lambda needs ECR's.
data "aws_ecr_image" "${id}" {
  provider = aws.${id}

  repository_name = aws_ecr_repository.${id}.name
  image_tag       = "\${local.${id}.image_tag}-\${terraform_data.${id}_image.id}"
}

resource "aws_iam_role" "${id}" {
  provider = aws.${id}

  name = local.${id}.connector_name
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { Service = "lambda.amazonaws.com" }
      Action    = "sts:AssumeRole"
    }]
  })
}

# The log group Lambda writes the connector's logs to.
resource "aws_cloudwatch_log_group" "${id}" {
  provider = aws.${id}

  name              = "/aws/lambda/\${local.${id}.connector_name}"
  retention_in_days = local.${id}.log_retention_days
}

# The connector writes its own logs, and no others.
resource "aws_iam_role_policy" "${id}_logs" {
  provider = aws.${id}

  name = "${POLICY_NAMES.logs}"
  role = aws_iam_role.${id}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["logs:CreateLogStream", "logs:PutLogEvents"]
      Resource = "\${aws_cloudwatch_log_group.${id}.arn}:*"
    }]
  })
}

# The connector reads the GitHub App's private key, and nothing else.
resource "aws_iam_role_policy" "${id}_private_key" {
  provider = aws.${id}

  name = "${POLICY_NAMES.privateKey}"
  role = aws_iam_role.${id}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = concat(
      [{
        Sid    = "ReadPrivateKey"
        Effect = "Allow"
        Action = "secretsmanager:GetSecretValue"
        # "??????" matches the six random characters that end the secret's
        # ARN, so the connector gets this one secret and no other.
        Resource = "\${local.${id}.private_key_secret_partial_arn}-??????"
      }],
      local.${id}.kms_key_arn == null ? [] : [{
        Sid      = "DecryptPrivateKey"
        Effect   = "Allow"
        Action   = "kms:Decrypt"
        Resource = local.${id}.kms_key_arn
        Condition = {
          StringEquals = {
            "kms:ViaService" = "secretsmanager.\${local.${id}.secrets_region}.\${data.aws_partition.${id}.dns_suffix}"
          }
        }
      }],
    )
  })${renderAwsSecretLifecycle(id, connector)}
}

# No vpc_config: the connector calls api.github.com over the internet.
resource "aws_lambda_function" "${id}" {
  provider = aws.${id}

  function_name = local.${id}.connector_name
  description   = "${toConnectorDescription(org)}"
  role          = aws_iam_role.${id}.arn
  package_type  = "Image"
  image_uri     = "\${aws_ecr_repository.${id}.repository_url}@\${data.aws_ecr_image.${id}.image_digest}"
  architectures = ["${LAMBDA_SETTINGS.architecture}"]
  memory_size   = ${LAMBDA_SETTINGS.memorySize}
  timeout       = ${LAMBDA_SETTINGS.timeout}

  # The connector's role can't create a log group, so its own must exist before
  # it runs.
  depends_on = [aws_cloudwatch_log_group.${id}]
}

# P0 invokes the connector as its role in this account, and needs nothing else
# on it.
resource "aws_iam_role_policy" "${id}_invoke" {
  provider = aws.${id}

  name = "\${local.${id}.connector_name}-invoke"
  role = local.${id}.p0_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "lambda:InvokeFunction"
      Resource = aws_lambda_function.${id}.arn
    }]
  })
}

# The connector hosting P0's installer asks for. It's ready once P0 can invoke
# the connector and the connector can read the key.
output "${id}" {
  value = {
    account_id       = local.${id}.account_id
    connector_name   = aws_lambda_function.${id}.function_name
    connector_region = local.${id}.connector_region
  }

  depends_on = [
    aws_iam_role_policy.${id}_invoke,
    aws_iam_role_policy.${id}_private_key,
  ]
}
`;
};

/** Where the private key's secret is, as P0 points the connector at it. */
type GcpPrivateKeySecret = Readonly<{
  /** The secret's name, its ID */
  name: string;
  /** The vault's project */
  projectId: string;
  /** `projects/<project>/secrets/<name>` (see `toSecretResourceName`) */
  resourceName: string;
}>;

/** A 32-bit FNV-1a hash of `text`, as 8 hex digits. It only keeps the
 * service accounts of two connectors with long, alike names apart, so it
 * needn't be a cryptographic hash. */
const toShortHash = (text: string) =>
  new TextEncoder()
    .encode(text)
    .reduce(
      (hash, byte) => Math.imul(hash ^ byte, 0x01000193) >>> 0,
      0x811c9dc5
    )
    .toString(16)
    .padStart(8, "0");

/** The ID of the connector's own service account. A service account's ID has
 * 6 to 30 characters, fewer than a Cloud Run service's name, so it's the
 * name's start and a hash of the whole name. */
export const toServiceAccountId = (connectorName: string) =>
  `${connectorName.slice(0, 21)}-${toShortHash(connectorName)}`;

/** One organization's connector on Google Cloud, as its Terraform and shell
 * script deploy it. Each value is trimmed, as P0 stores it. */
export type GcpConnector = Readonly<{
  connectorName: string;
  connectorRegion: string;
  /** The image Cloud Run deploys, pinned by its digest */
  image: string;
  imageTag: string;
  /** The GitHub organization's login */
  org: string;
  /** P0's service account, which invokes the connector */
  p0ServiceAccountEmail: string;
  /** The project the connector runs in */
  projectId: string;
  secret: GcpPrivateKeySecret;
  /** The ID of the connector's own service account, in its project */
  serviceAccountId: string;
}>;

/**
 * Resolves the connector that one GitHub organization's Terraform and shell
 * script deploy on Cloud Run, for the customer to deploy in their own cloud.
 *
 * Both grant the connector's service account read on the one secret that
 * `privateKeySecretName` names in the vault's project, and let P0's service
 * account, which the installed gcloud integration records, invoke the
 * connector and read its service.
 *
 * @throws ValidationError, with the input's path as its form id, for the first
 * input that the connector couldn't deploy with: a field P0 would refuse, a
 * missing `privateKeySecretName`, an image that isn't published yet, or no
 * gcloud integration to invoke the connector as.
 */
export const toGcpConnector = (args: ConnectorArgs<"gcp">): GcpConnector => {
  const { context, imageDigest = IMAGE_DIGESTS.gcp, inputs, org } = args;
  validateLocation(args);
  const { privateKeySecretName: name, vault } = inputs;
  validateSecretName(name, "gcp-sm");
  validateImageDigest(imageDigest);
  const p0ServiceAccountEmail = p0InvokerEmail(context.installed);
  if (!p0ServiceAccountEmail) {
    throw new ValidationError(
      "hosting",
      {},
      "Install P0's Google Cloud integration first. P0 invokes the connector as that integration's service account."
    );
  }
  const [imageTag, digest] = imageDigest.split("@");
  const connectorName = inputs.hosting.connectorName.trim();
  return {
    connectorName,
    connectorRegion: inputs.hosting.connectorRegion.trim(),
    image: `${CLOUD_RUN_IMAGE}@${digest}`,
    imageTag,
    org,
    p0ServiceAccountEmail,
    projectId: inputs.hosting.projectId.trim(),
    secret: {
      name: name.trim(),
      projectId: vault.install.trim(),
      resourceName: toSecretResourceName({ name, projectId: vault.install }),
    },
    serviceAccountId: toServiceAccountId(connectorName),
  };
};

/**
 * Renders the Terraform that deploys one GitHub organization's connector on
 * Cloud Run.
 *
 * It deploys the pinned image from Docker Hub as a Cloud Run service with a
 * service account of its own, which can read the GitHub App's private key
 * secret and nothing else. P0's service account can invoke the service, and
 * read it to find its URL. An output gives the connector hosting to enter in
 * the installer.
 *
 * Every Terraform name carries the org's login, so several orgs' connectors
 * can share a configuration.
 */
export const renderGcpTerraform = (connector: GcpConnector): string => {
  const {
    connectorName,
    connectorRegion,
    image,
    imageTag,
    org,
    p0ServiceAccountEmail,
    projectId,
    secret,
    serviceAccountId,
  } = connector;
  const id = toTfId(org);

  return `# P0's GitHub connector for ${org}, on Google Cloud Run.
#
# Applying this deploys P0's connector image from Docker Hub as a Cloud Run
# service in project ${projectId}. Run terraform with Google credentials that can
# enable APIs (Service Usage) and manage Cloud Run, service accounts and IAM in
# project ${projectId}, and IAM on the private key's secret in project
# ${secret.projectId}.
#
# P0 runs one connector for each organization, so apply this once, in one
# cloud. Other organizations' connectors can share this configuration, but each
# needs its own connector name: the connector's service and service account are
# named after it, and these names are unique in a Google Cloud project.

terraform {
  # If your configuration already has a required_providers block, add google to
  # it and delete this block.
  required_providers {
    google = {
      source = "hashicorp/google"
      # Cloud Run services' deletion_protection needs 6.0.
      version = ">= 6.0"
    }
  }
}

locals {
  ${id} = {
    # Where P0 invokes the connector, as entered in P0's installer. P0 calls
    # the connector exactly here, so don't change these: to change one, remove
    # the organization in P0 and add it again.
    project_id       = "${projectId}"
    connector_name   = "${connectorName}"
    connector_region = "${connectorRegion}"
    # The secret that holds the GitHub App's private key, by its name in its
    # project. P0 gives the connector its resource name,
    # ${secret.resourceName}.
    # The connector can read this secret and no other.
    private_key_secret_id = "${secret.name}"
    secrets_project_id    = "${secret.projectId}"
    # The connector's own service account, named after the connector.
    service_account_id = "${serviceAccountId}"
    # P0's service account, which invokes the connector.
    p0_service_account_email = "${p0ServiceAccountEmail}"
    # P0's connector image (${imageTag}), pinned by its digest.
    image = "${image}"
  }
}

resource "google_project_service" "${id}" {
  for_each = toset([
    "iam.googleapis.com",
    "run.googleapis.com",
    "secretmanager.googleapis.com",
  ])

  project            = local.${id}.project_id
  service            = each.key
  disable_on_destroy = false
}

resource "google_service_account" "${id}" {
  project      = local.${id}.project_id
  account_id   = local.${id}.service_account_id
  display_name = "${toConnectorDescription(org)}"

  depends_on = [google_project_service.${id}]
}

# The connector reads the private key's value (secretmanager.versions.access),
# then its version (secretmanager.versions.get). The Secret Accessor role has
# only the first, so the Viewer role is granted too, on this one secret.
resource "google_secret_manager_secret_iam_member" "${id}_accessor" {
  project   = local.${id}.secrets_project_id
  secret_id = local.${id}.private_key_secret_id
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${google_service_account.${id}.email}"
}

resource "google_secret_manager_secret_iam_member" "${id}_viewer" {
  project   = local.${id}.secrets_project_id
  secret_id = local.${id}.private_key_secret_id
  role      = "roles/secretmanager.viewer"
  member    = "serviceAccount:\${google_service_account.${id}.email}"
}

# No vpc_access: the connector calls api.github.com over the internet.
resource "google_cloud_run_v2_service" "${id}" {
  project             = local.${id}.project_id
  name                = local.${id}.connector_name
  location            = local.${id}.connector_region
  description         = "${toConnectorDescription(org)}"
  deletion_protection = false
  # P0 calls the connector from outside Google Cloud. IAM, not the network,
  # decides who can invoke it.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.${id}.email

    containers {
      image = local.${id}.image

      # The connector checks each caller's identity token against this
      # account, on top of Cloud Run's roles/run.invoker check.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = local.${id}.p0_service_account_email
      }
    }
  }

  depends_on = [google_project_service.${id}]
}

# P0 invokes the connector as its service account, and needs nothing else on
# it.
resource "google_cloud_run_v2_service_iam_member" "${id}_invoker" {
  project  = google_cloud_run_v2_service.${id}.project
  location = google_cloud_run_v2_service.${id}.location
  name     = google_cloud_run_v2_service.${id}.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:\${local.${id}.p0_service_account_email}"
}

# P0 reads the service to find its URL when it installs. The invoker role can't
# read it.
resource "google_cloud_run_v2_service_iam_member" "${id}_viewer" {
  project  = google_cloud_run_v2_service.${id}.project
  location = google_cloud_run_v2_service.${id}.location
  name     = google_cloud_run_v2_service.${id}.name
  role     = "roles/run.viewer"
  member   = "serviceAccount:\${local.${id}.p0_service_account_email}"
}

# The connector hosting P0's installer asks for. It's ready once P0 can invoke
# and read the service and the connector can read the key.
output "${id}" {
  value = {
    project_id       = google_cloud_run_v2_service.${id}.project
    connector_name   = google_cloud_run_v2_service.${id}.name
    connector_region = google_cloud_run_v2_service.${id}.location
  }

  depends_on = [
    google_cloud_run_v2_service_iam_member.${id}_invoker,
    google_cloud_run_v2_service_iam_member.${id}_viewer,
    google_secret_manager_secret_iam_member.${id}_accessor,
    google_secret_manager_secret_iam_member.${id}_viewer,
  ]
}
`;
};
